import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
  Avatar,
  Stack
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  Visibility as PreviewIcon,
  MoreHoriz as MoreIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Restaurant as RestaurantIcon,
  LocalActivity as ActivityIcon,
  Hotel as HotelIcon
} from '@mui/icons-material';
import ItemPreviewModal from './ItemPreviewModal';

const AIResultsCardSlider = ({ 
  title, 
  items = [], 
  category, 
  dayIndex, 
  onSelect, 
  selectedIndices = [],
  maxVisible = 3
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Calculate how many items to show
  const visibleItems = isMobile ? 1 : maxVisible;
  const totalPages = Math.ceil(items.length / visibleItems);
  const hasMore = items.length > visibleItems;

  // Check scroll position
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, [items]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const cardWidth = isMobile ? 320 : 350;
      const gap = 16;
      const scrollAmount = (cardWidth + gap) * visibleItems;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handlePreview = (item, event) => {
    event.stopPropagation();
    setPreviewItem(item);
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewItem(null);
  };

  const handlePreviewSelect = () => {
    if (previewItem) {
      onSelect(category, dayIndex, items.findIndex(item => item === previewItem));
    }
    handlePreviewClose();
  };

  const getCategoryIcon = (category) => {
    const icons = {
      locations: <LocationIcon />,
      restaurants: <RestaurantIcon />,
      activities: <ActivityIcon />,
      hotels: <HotelIcon />
    };
    return icons[category] || <LocationIcon />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      locations: '#1976d2',
      restaurants: '#ed6c02',
      activities: '#9c27b0',
      hotels: '#2e7d32'
    };
    return colors[category] || '#1976d2';
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header with title and scroll buttons */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        px: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ 
            bgcolor: getCategoryColor(category), 
            width: 32, 
            height: 32 
          }}>
            {getCategoryIcon(category)}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          <Chip 
            label={`${items.length} options`}
            size="small"
            sx={{ 
              bgcolor: getCategoryColor(category) + '20',
              color: getCategoryColor(category),
              fontWeight: 500
            }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            sx={{
              backgroundColor: canScrollLeft ? 'rgba(0,0,0,0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: canScrollLeft ? 'rgba(0,0,0,0.2)' : 'transparent',
              },
              opacity: canScrollLeft ? 1 : 0.3,
              transition: 'all 0.2s ease'
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          
          <IconButton
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            sx={{
              backgroundColor: canScrollRight ? 'rgba(0,0,0,0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: canScrollRight ? 'rgba(0,0,0,0.2)' : 'transparent',
              },
              opacity: canScrollRight ? 1 : 0.3,
              transition: 'all 0.2s ease'
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Horizontal scrollable container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          overflowY: 'visible',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          pb: 3,
          px: 1,
          pt: 2
        }}
      >
        {items.map((item, index) => {
          const isSelected = selectedIndices.includes(index);
          
          return (
            <Card
              key={index}
              onClick={() => onSelect(category, dayIndex, index)}
              sx={{
                minWidth: isMobile ? 300 : 350,
                maxWidth: isMobile ? 300 : 350,
                cursor: 'pointer',
                border: isSelected ? `3px solid ${getCategoryColor(category)}` : '2px solid #e0e0e0',
                backgroundColor: isSelected ? getCategoryColor(category) + '10' : '#ffffff',
                borderRadius: 3,
                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 0.3s ease',
                boxShadow: isSelected ? theme.shadows[6] : theme.shadows[2],
                '&:hover': {
                  transform: 'scale(1.03)',
                  boxShadow: theme.shadows[8],
                  border: `3px solid ${getCategoryColor(category)}80`,
                  backgroundColor: isSelected ? getCategoryColor(category) + '20' : '#f8f9fa',
                },
                position: 'relative',
                overflow: 'visible'
              }}
            >
              {/* Selection indicator */}
              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: getCategoryColor(category),
                    color: 'white',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    border: '3px solid white',
                    '&:hover': {
                      backgroundColor: getCategoryColor(category),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CheckIcon sx={{ fontSize: 18 }} />
                </Box>
              )}

              <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                {/* Item name */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1.5, 
                    fontSize: '1.1rem',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    color: 'text.primary'
                  }}
                >
                  {item.name}
                </Typography>

                {/* Location/City */}
                {item.city && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ fontSize: '0.9rem', fontWeight: 500 }}
                    >
                      {item.city}
                    </Typography>
                  </Box>
                )}

                {/* Description */}
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    mb: 2, 
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {item.description}
                </Typography>

                {/* Rating and Price */}
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                  {item.rating && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <StarIcon sx={{ fontSize: 16, color: '#ff9800', mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {item.rating}
                      </Typography>
                    </Box>
                  )}
                  
                  {item.priceRange && (
                    <Chip
                      label={item.priceRange}
                      size="small"
                      sx={{ 
                        backgroundColor: getCategoryColor(category) + '20',
                        color: getCategoryColor(category),
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        border: `1px solid ${getCategoryColor(category)}40`,
                        '&:hover': {
                          backgroundColor: getCategoryColor(category) + '30',
                        }
                      }}
                    />
                  )}
                </Stack>

                {/* Why recommended */}
                {item.whyRecommended && (
                  <Box sx={{ 
                    bgcolor: getCategoryColor(category) + '10',
                    p: 1.5,
                    borderRadius: 2,
                    mb: 2,
                    border: `1px solid ${getCategoryColor(category)}30`
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.85rem',
                        fontStyle: 'italic',
                        color: getCategoryColor(category),
                        fontWeight: 600,
                        lineHeight: 1.4
                      }}
                    >
                      💡 {item.whyRecommended}
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons */}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={(e) => handlePreview(item, e)}
                    sx={{
                      flex: 1,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      borderColor: getCategoryColor(category),
                      color: getCategoryColor(category),
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: getCategoryColor(category) + '20',
                        borderColor: getCategoryColor(category),
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Preview
                  </Button>
                  
                  {isSelected && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CheckIcon />}
                      sx={{
                        bgcolor: getCategoryColor(category),
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: getCategoryColor(category),
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }
                      }}
                    >
                      Selected
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })}

        {/* More results indicator */}
        {hasMore && (
          <Card
            sx={{
              minWidth: isMobile ? 300 : 350,
              maxWidth: isMobile ? 300 : 350,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px dashed ${getCategoryColor(category)}40`,
              backgroundColor: getCategoryColor(category) + '05',
              borderRadius: 3,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: getCategoryColor(category) + '10',
                border: `2px dashed ${getCategoryColor(category)}60`,
                transform: 'scale(1.02)'
              },
              transition: 'all 0.3s ease'
            }}
            onClick={() => scroll('right')}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <MoreIcon sx={{ fontSize: 48, color: getCategoryColor(category), mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: getCategoryColor(category), mb: 1 }}>
                More Options
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {items.length - visibleItems} more {title.toLowerCase()}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Scroll indicators */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 2,
        gap: 0.5
      }}>
        {Array.from({ length: totalPages }).map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: canScrollLeft || canScrollRight ? getCategoryColor(category) : 'grey.300',
              opacity: 0.6
            }}
          />
        ))}
      </Box>

      {/* Preview Modal */}
      <ItemPreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        item={previewItem}
        category={category}
        onSelect={handlePreviewSelect}
      />
    </Box>
  );
};

export default AIResultsCardSlider;
